import { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { SectionHeader } from "@/components/SectionHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCheck, Bell, Send } from "lucide-react";
import { Target, CheckCircle2, XCircle, Info } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

const ICON = {
  target: Target,
  approval: CheckCircle2,
  rejection: XCircle,
  system: Info,
  broadcast: Bell,
};

const TONE = {
  approval: "text-[hsl(var(--success))]",
  rejection: "text-[hsl(var(--danger))]",
  target: "text-primary",
  system: "text-muted-foreground",
  broadcast: "text-primary",
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({
    title: "", message: "", priority: "medium",
    target_type: "role", role: "all",
    user_ids: [], under_manager_id: "", under_team_leader_id: "",
  });

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user.role === "admin") {
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [user.role]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (err) {
      logger.error("Failed to mark notification as read:", err);
      toast.error("Could not mark as read");
    }
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All notifications marked as read");
      load();
    } catch (err) {
      logger.error("Failed to mark all notifications as read:", err);
      toast.error("Could not mark all as read");
    }
  };

  const sendBroadcast = async () => {
    if (!compose.title.trim() || !compose.message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    const body = { title: compose.title, message: compose.message, priority: compose.priority };
    if (compose.target_type === "role") body.role = compose.role;
    else if (compose.target_type === "manager") body.under_manager_id = compose.under_manager_id;
    else if (compose.target_type === "team_leader") body.under_team_leader_id = compose.under_team_leader_id;
    else if (compose.target_type === "users") body.user_ids = compose.user_ids;
    try {
      const { data } = await api.post("/notifications/broadcast", body);
      toast.success(`Notification sent to ${data.recipients} recipient${data.recipients !== 1 ? "s" : ""}`);
      setComposeOpen(false);
      setCompose({ ...compose, title: "", message: "" });
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const isAdmin = user.role === "admin";
  const managers = users.filter((u) => u.role === "manager");
  const leaders = users.filter((u) => u.role === "team_leader");

  return (
    <div data-testid="notifications-page">
      <SectionHeader
        title="Notifications"
        description="Recent alerts related to your account."
        actions={
          <div className="flex gap-2">
            {isAdmin && (
              <Button onClick={() => setComposeOpen(true)} data-testid="compose-notification">
                <Send className="mr-2 h-4 w-4" /> Compose
              </Button>
            )}
            <Button variant="secondary" onClick={readAll} data-testid="mark-all-read">
              <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
            </Button>
          </div>
        }
      />
      <Card className="rounded-xl border border-border bg-card p-2 shadow-sm">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Bell className="mb-3 h-8 w-8" strokeWidth={1.5} />
            <p className="text-sm">You’re all caught up.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((n) => {
              const Icon = ICON[n.type] || Info;
              return (
                <li
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  className={cn("flex items-start gap-4 rounded-md p-4 transition-colors", !n.read && "bg-primary/5")}
                >
                  <div className={cn("mt-0.5 rounded-md bg-muted p-2", TONE[n.type] || "")}>
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-medium">{n.title}</div>
                      {!n.read && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">NEW</span>}
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{n.message}</p>
                    <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">{formatDateTime(n.created_at)}</div>
                  </div>
                  {!n.read && (
                    <Button size="sm" variant="ghost" onClick={() => markRead(n.id)}>Mark read</Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Admin Compose */}
      {isAdmin && (
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent className="max-w-xl" data-testid="compose-dialog">
            <DialogHeader><DialogTitle>Send notification</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input data-testid="compose-title" value={compose.title} onChange={(e) => setCompose({ ...compose, title: e.target.value })} placeholder="Announcement title" />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea data-testid="compose-message" rows={4} value={compose.message} onChange={(e) => setCompose({ ...compose, message: e.target.value })} placeholder="Write your message…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={compose.priority} onValueChange={(v) => setCompose({ ...compose, priority: v })}>
                    <SelectTrigger data-testid="compose-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Recipients</Label>
                  <Select value={compose.target_type} onValueChange={(v) => setCompose({ ...compose, target_type: v })}>
                    <SelectTrigger data-testid="compose-target-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="role">By role</SelectItem>
                      <SelectItem value="manager">Under a Manager</SelectItem>
                      <SelectItem value="team_leader">Under a Team Leader</SelectItem>
                      <SelectItem value="users">Individual users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {compose.target_type === "role" && (
                <div className="space-y-1.5">
                  <Label>Send to role</Label>
                  <Select value={compose.role} onValueChange={(v) => setCompose({ ...compose, role: v })}>
                    <SelectTrigger data-testid="compose-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="manager">All Managers</SelectItem>
                      <SelectItem value="team_leader">All Team Leaders</SelectItem>
                      <SelectItem value="sales_executive">All Sales Executives</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {compose.target_type === "manager" && (
                <div className="space-y-1.5">
                  <Label>Manager hierarchy</Label>
                  <Select value={compose.under_manager_id} onValueChange={(v) => setCompose({ ...compose, under_manager_id: v })}>
                    <SelectTrigger data-testid="compose-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                    <SelectContent>
                      {managers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {compose.target_type === "team_leader" && (
                <div className="space-y-1.5">
                  <Label>Team Leader</Label>
                  <Select value={compose.under_team_leader_id} onValueChange={(v) => setCompose({ ...compose, under_team_leader_id: v })}>
                    <SelectTrigger data-testid="compose-tl"><SelectValue placeholder="Select TL" /></SelectTrigger>
                    <SelectContent>
                      {leaders.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {compose.target_type === "users" && (
                <div className="space-y-1.5">
                  <Label>Users ({compose.user_ids.length} selected)</Label>
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                    {users.map((u) => {
                      const on = compose.user_ids.includes(u.id);
                      return (
                        <label key={u.id} data-testid={`compose-user-${u.employee_id}`} className={cn("flex items-center gap-3 rounded-md p-1.5 text-sm cursor-pointer", on && "bg-primary/10")}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => {
                              const next = on
                                ? compose.user_ids.filter((x) => x !== u.id)
                                : [...compose.user_ids, u.id];
                              setCompose({ ...compose, user_ids: next });
                            }}
                          />
                          <span className="flex-1">{u.name}</span>
                          <span className="text-[10px] text-muted-foreground tabular-nums">{u.employee_id}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button onClick={sendBroadcast} data-testid="compose-send">
                <Send className="mr-2 h-4 w-4" /> Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
